import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  InputGroup,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { createBooleanPropertyShell } from '@finley-technologies/credit-agreement-utils';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  CheckboxFormField,
  FormFieldLabel,
  InputFormField,
} from 'packages/common-frontend/components/FormField';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const createUserEntityShell = (): CreditAgreement.DomainEntity.Items.UserEntity => ({
  entity: {
    id: uuidv4(),
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email@email.com',
  },
  primaryContact: createBooleanPropertyShell('primaryContact'),
  _isWrittenIn_: true,
});

type EditUserModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  organizationId: string;
};
export const EditUserModal = ({
  isOpen,
  setIsOpen,
  wipConfig: {
    formProps,
    organizationIdToUsersMap,
    saveCreditAgreementFullArtifactWip,
  },
  basePropertyKey,
  organizationId,
}: EditUserModalProps) => {
  const users = organizationIdToUsersMap[organizationId];
  const selectOptions = _.sortBy(
    _.map(users, (user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
    })),
    'label',
  );

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="3xl" width="2xl">
        <ModalHeader>Edit User</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex alignItems="flex-end" flexDirection="row" mt={5}>
            <FormFieldLabel label="User" labelWidth="60" />
            <InputGroup width="100%">
              <Select
                value={getIn(formProps.values, `${basePropertyKey}.entity.id`)}
                onChange={(val) => {
                  const currentUsersEntity = getIn(
                    formProps.values,
                    basePropertyKey,
                  );
                  const properties = _.omit(currentUsersEntity, 'entity');
                  const userEntity = _.find(
                    organizationIdToUsersMap[organizationId],
                    (user) => user.id === val,
                  );
                  const userEntityShell = createUserEntityShell();
                  formProps.setFieldValue(`${basePropertyKey}`, {
                    ...userEntityShell,
                    entity: userEntity,
                    ...properties,
                  });
                }}
                options={selectOptions}
                width="sm"
              />
            </InputGroup>
          </Flex>
          <InputFormField
            propName={`${basePropertyKey}.section`}
            label="Section"
            formProps={formProps}
            value={getIn(formProps.values, `${basePropertyKey}.section`)}
            labelWidth="60"
            inputWidth="sm"
            flexDirection="row"
            alignItems="flex-end"
            marginTop={5}
          />
          <CheckboxFormField
            propName={`${basePropertyKey}._isWrittenIn_`}
            label="Is Written In"
            formProps={formProps}
            value={getIn(formProps.values, `${basePropertyKey}._isWrittenIn_`)}
            labelWidth="60"
            inputWidth="sm"
            flexDirection="row"
            marginTop={5}
          />

          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: formProps.values.id,
                      creditFacilityId: formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(formProps.values),
                    },
                  },
                });
                setIsOpen(false);
              }}
              aria-label="Done"
              isDisabled={_.keys(formProps.errors).length > 0}
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
